import { useState } from "react";
import { Search, Briefcase, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { JobMeta } from "@/components/JobForm";
import { api, formatErr, fmtDate, useFetch } from "@/lib/api";

const ApplyDialog = ({ job, onClose, onApplied }) => {
  const [note, setNote] = useState("");
  const send = async () => {
    try {
      await api.post(`/jobs/${job.id}/apply`, { cover_note: note });
      toast.success("Application sent");
      onApplied(job.id);
      setNote("");
      onClose();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="job-apply-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">{job?.title}</DialogTitle><DialogDescription>{job?.business?.name}</DialogDescription></DialogHeader>
        {job && <JobMeta j={job} />}
        <p className="whitespace-pre-wrap text-sm text-stone-700">{job?.description}</p>
        {job?.requirements && <p className="whitespace-pre-wrap text-sm text-stone-600"><b>Requirements:</b> {job.requirements}</p>}
        {!job?.applied && <Textarea data-testid="job-cover-note-input" rows={4} placeholder="Why you're a great fit (your profile, resume and licenses are shared automatically)" value={note} onChange={(e) => setNote(e.target.value)} />}
        <DialogFooter>{job?.applied ? <p className="flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" />You applied</p>
          : <Button data-testid="job-apply-submit" onClick={send}>Submit application</Button>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function CandidateJobs() {
  const { data, setData } = useFetch("/jobs");
  const { data: apps, reload } = useFetch("/me/job-applications");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const shown = (data || []).filter((j) => `${j.title} ${j.category} ${j.description} ${j.business?.name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader eyebrow="Job board" title="Find jobs" desc="Roles posted by member businesses and approved by our team." />
      <Tabs defaultValue="board">
        <TabsList className="mb-6">
          <TabsTrigger value="board" data-testid="tab-job-board">Open jobs ({data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-my-applications">My applications ({apps?.length ?? 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="board">
          <div className="relative mb-4 max-w-sm"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <Input data-testid="job-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs" className="bg-white pl-9" /></div>
          {data?.length === 0 && <EmptyState testId="job-board-empty" icon={Briefcase} title="No open jobs right now" desc="Check back soon — we'll also email you when you're suggested for a role." />}
          <div className="grid gap-4 md:grid-cols-2">
            {shown.map((j) => (
              <div key={j.id} data-testid={`board-job-${j.id}`} className={`lift flex flex-col rounded-md border p-6 ${j.featured ? "border-amber-300 bg-amber-50/40" : "border-stone-200 bg-white"}`}>
                {j.featured && <span data-testid={`board-featured-${j.id}`} className="mb-2 w-fit rounded-full bg-amber-500 px-2.5 py-0.5 text-[10px] font-mono uppercase text-slate-900">Featured</span>}
                <p className="font-display text-lg font-bold">{j.title}</p>
                <p className="text-sm text-stone-600">{j.business?.name}</p>
                <div className="mt-1"><JobMeta j={j} /></div>
                <p className="mt-3 line-clamp-3 text-sm text-stone-600">{j.description}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-[11px] font-mono text-stone-400">{fmtDate(j.created_at)}</span>
                  <Button size="sm" data-testid={`view-job-${j.id}`} variant={j.applied ? "outline" : "default"} onClick={() => { setOpen(j); api.post(`/jobs/${j.id}/view`).catch(() => {}); }}>{j.applied ? "Applied" : "View & apply"}</Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="mine" className="space-y-3">
          {apps?.length === 0 && <p className="text-sm text-stone-500">You haven't applied to any jobs yet.</p>}
          {(apps || []).map((a) => (
            <div key={a.id} data-testid={`my-app-${a.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-stone-200 bg-white p-5">
              <div><p className="font-medium">{a.job.title}</p><p className="text-xs text-stone-500">{a.business.name} · applied {fmtDate(a.created_at)}</p></div>
              <StatusBadge status={a.status} />
            </div>
          ))}
        </TabsContent>
      </Tabs>
      <ApplyDialog job={open} onClose={() => setOpen(null)} onApplied={(id) => { setData((p) => p.map((x) => (x.id === id ? { ...x, applied: true } : x))); reload(); }} />
    </div>
  );
}
